// The funnel's answers (the prototype's "global state"): everything the visitor answers and every test result,
// keyed as in docs/api-contract.md, persisted in localStorage["lola_funnel_answers"].
// It lives next to the production GlobalStateProvider, not instead of it: the production state keeps what the
// production code reads (email, language, name, level … → web-onboarding-email / -finish, the paywall) and is
// filled from these answers by useProductionSync (./productionSync.ts).
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useStaticText } from "../../providers/staticText/staticTextProvider";

type Answers = Record<string, unknown>;
const STORAGE_KEY = "lola_funnel_answers";

function loadAnswers(): Answers {
  try {
    // ?reset in the URL starts a clean run (handy when reviewing)
    if (new URLSearchParams(window.location.search).has("reset")) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* storage unavailable */
      }
      return {};
    }
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

type FunnelState = {
  answers: Answers;
  setAnswer: (key: string, value: unknown) => void;
};

const FunnelStateContext = createContext<FunnelState | null>(null);

export function FunnelStateProvider({ children }: { children: ReactNode }) {
  const [answers, setAnswers] = useState<Answers>(loadAnswers);
  const { languageCode } = useStaticText();

  const setAnswer = useCallback((key: string, value: unknown) => {
    setAnswers((prev) => {
      const next = { ...prev, [key]: value };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // storage can be unavailable (private mode) — answers then live for the session only
      }
      return next;
    });
  }, []);

  // Arabic is right-to-left; the screens are built for both directions.
  useEffect(() => {
    document.documentElement.lang = languageCode;
    document.documentElement.dir = languageCode === "ar" ? "rtl" : "ltr";
  }, [languageCode]);

  const value = useMemo<FunnelState>(() => ({ answers, setAnswer }), [answers, setAnswer]);
  return <FunnelStateContext.Provider value={value}>{children}</FunnelStateContext.Provider>;
}

export function useFunnelState() {
  const ctx = useContext(FunnelStateContext);
  if (!ctx) throw new Error("useFunnelState must be used inside FunnelStateProvider");
  return ctx;
}
